import React, { useCallback, useMemo } from "react";

import {
  snapToWholePeriods,
  SPEND_TIME_UNIT,
  SpendFilters,
} from "lib/hooks/api/spend/spendFilters";
import { useSpendOverTime } from "lib/hooks/api/spend/useSpendOverTime";
import { useTopSpendBuyers } from "lib/hooks/api/spend/useTopSpendBuyers";
import { TrackingProvider } from "lib/tracking";
import { SpendGroupBy } from "lib/types/graphQLEnums";
import { ORDER_BY, SORT_ORDER, SpendRankingSort } from "./hooks/useRankingTable";
import { SpendChartType } from "./hooks/useSpendUrlState";
import { SpendChartError } from "./pageStates";
import SpendChart from "./SpendChart";
import { buildGroupedSpendDatasets } from "./spendChartUtils";

type Props = {
  filters: SpendFilters;
  dateUnit: "month" | "quarter" | "year";
  /** The ranking's order: the chart plots its top ten, by the measure it ranks on. */
  sort: SpendRankingSort;
  /** The shape to draw. */
  chartType: SpendChartType;
};

function BuyerChart({ filters, dateUnit, sort, chartType }: Props) {
  const { measure } = sort;
  const hasDateRange = Boolean(filters.dateFrom || filters.dateTo);
  const {
    data: top10Buyers,
    isError: isBuyersError,
    error: buyersError,
    refetch: refetchBuyers,
  } = useTopSpendBuyers(
    {
      filters,
      limit: 10,
      offset: 0,
      orderBy: ORDER_BY[sort.measure],
      sortOrder: SORT_ORDER[sort.sortOrder],
    },
    { enabled: hasDateRange },
  );
  const {
    data: overTime,
    isLoading,
    isError: isOverTimeError,
    error: overTimeError,
    refetch: refetchOverTime,
  } = useSpendOverTime(
    {
      // Whole periods only, so a range starting mid-quarter doesn't chart its first
      // quarter as a dip. The ranking above still uses the range as set.
      filters: {
        ...snapToWholePeriods(filters, dateUnit),
        buyerGuids: top10Buyers?.results.map((b) => b.buyerGuid),
      },
      dateUnit: SPEND_TIME_UNIT[dateUnit],
      groupBy: SpendGroupBy.Buyer,
    },
    { enabled: hasDateRange && !!top10Buyers },
  );

  const isError = isBuyersError || isOverTimeError;
  const chartError = buyersError ?? overTimeError;
  const onRetry = useCallback(() => {
    void refetchBuyers();
    void refetchOverTime();
  }, [refetchBuyers, refetchOverTime]);

  const chartData = useMemo(
    () => (overTime ? buildGroupedSpendDatasets(overTime.points, measure) : []),
    [overTime, measure],
  );

  return (
    <TrackingProvider data={{ "Context source": "Buyer spending chart" }}>
      {isError ? (
        <SpendChartError error={chartError} onRetry={onRetry} />
      ) : (
        <SpendChart
          data={chartData}
          dateUnit={dateUnit}
          isLoading={isLoading}
          measure={measure}
          chartType={chartType}
        />
      )}
    </TrackingProvider>
  );
}

export default BuyerChart;
